import { useState, type ReactNode } from "react";

import classes from "./CardPicture.module.css";

export interface CardPictureProps {
  src: string;
  alt: string;
  /** Called when the picture fails to load (a list reads its signed URLs again). */
  onFailed?: (() => void) | undefined;
  /** Shown over the picture once it fails to load (a file the bucket cannot give), in place of
   *  the browser's broken image, until it loads after all; none keeps the failed picture. */
  fallback?: ReactNode;
}

/**
 * A grid card's picture, lazy and decoded off the main thread (a page's pictures load as they
 * near the view, issue 794), under live's skeleton until it has loaded or failed (`CardSkeleton`):
 * grey, not white, while a page's pictures arrive, and never painting top-down. After, a picture
 * with clear parts shows the card through them, not the grey. One already in the cache (a card
 * scrolled back into view) shows at once. It fills its box: the card gives it its shape.
 */
export function CardPicture({ src, alt, onFailed, fallback }: CardPictureProps) {
  const [shown, setShown] = useState<string | null>(null);
  // A picture that failed stays mounted in its box, unseen under the fallback: the media cookies'
  // retry asks again for that very element once a renewal lands (media-cookies Design 4), and
  // its load takes the fallback away.
  const [failed, setFailed] = useState<string | null>(null);
  const covered = failed === src && fallback !== undefined;
  return (
    <>
      <img
        ref={(img) => {
          if (img !== null && img.complete && img.naturalWidth > 0 && shown !== src) setShown(src);
        }}
        src={src}
        alt={alt}
        className={classes.picture}
        data-failed={covered || undefined}
        aria-hidden={covered || undefined}
        loading="lazy"
        decoding="async"
        onLoad={() => {
          setShown(src);
          setFailed(null);
        }}
        onError={() => {
          setShown(src);
          setFailed(src);
          onFailed?.();
        }}
      />
      {shown === src ? null : <span className={classes.skeleton} aria-hidden />}
      {covered ? <span className={classes.fallback}>{fallback}</span> : null}
    </>
  );
}
